import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, InputGroup } from "react-bootstrap";
import axios from "axios";
import config from '../../Common/Configurations/APIConfig';

const daysOfWeekMap = [
    { id: 0, name: "Sunday" }, { id: 1, name: "Monday" }, { id: 2, name: "Tuesday" },
    { id: 3, name: "Wednesday" }, { id: 4, name: "Thursday" }, { id: 5, name: "Friday" }, { id: 6, name: "Saturday" }
];

const SalonTimings = () => {
    const [salons, setSalons] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [btnLoading, setBtnLoading] = useState(false);

    // Modals
    const [showEditModal, setShowEditModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    // Data for Modals
    const [currentTimings, setCurrentTimings] = useState([]);
    const [selectedSalon, setSelectedSalon] = useState(null);

    const THEME_COLOR = '#065f46'; // Emerald Green
    const LIGHT_BG = '#f8fafc';

    const formatTime12h = (timeStr) => {
        if (!timeStr) return '---';
        let [hours, minutes] = timeStr.split(':');
        let h = parseInt(hours, 10);
        const ampm = h >= 12 ? 'PM' : 'AM';
        h = h % 12 || 12;
        return `${h.toString().padStart(2, '0')}:${minutes} ${ampm}`;
    };

    const loadInitialData = async () => {
        try {
            const res = await axios.get(config.APIACTIVEURL + config.GETALLSALON);
            setSalons(res.data || []);
        } catch (error) { console.error(error); }
    };

    useEffect(() => { loadInitialData(); }, []);

    const fetchSalonTimings = async (salon) => {
        setSelectedSalon(salon);
        try {
            const res = await axios.get(config.APIACTIVEURL + config.GETTIMINGBYSALONID + salon.salonId);
            const data = res.data || [];
            const fullWeek = daysOfWeekMap.map(day => {
                const found = data.find(d => d.dayOfWeek === day.id);
                return found || {
                    timingId: '00000000-0000-0000-0000-000000000000',
                    salonId: salon.salonId,
                    dayOfWeek: day.id,
                    openTime: '09:00', closeTime: '18:00', isClosed: false
                };
            });
            setCurrentTimings(fullWeek);
        } catch (error) { console.error(error); }
    };

    const filteredSalons = salons.filter(s => s.name.toLowerCase().includes(searchTerm.toLowerCase()));

    const handleAction = async (salon, mode) => {
        await fetchSalonTimings(salon);
        if (mode === 'view') setShowViewModal(true);
        else if (mode === 'edit') setShowEditModal(true);
        else setShowDeleteModal(true);
    };

    const handleSave = async () => {
        setBtnLoading(true);
        try {
            for (const item of currentTimings) {
                const isNew = item.timingId === "00000000-0000-0000-0000-000000000000";
                await axios({
                    method: isNew ? 'POST' : 'PUT',
                    url: config.APIACTIVEURL + (isNew ? config.CREATETIMING : config.UPDATETIMING),
                    data: item
                });
            }
            setShowEditModal(false);
        } catch (error) { alert("Error saving data"); }
        finally { setBtnLoading(false); }
    };

    return (
        <div className="page-content" style={{ backgroundColor: LIGHT_BG, minHeight: '100vh', paddingTop: "100px" }}>
            <div className="container">
                <div className="row mb-4 align-items-center">
                    <div className="col-md-6">
                        <h3 className="fw-bold mb-0" style={{ color: THEME_COLOR }}>Salon Timings</h3>
                        <p className="text-muted mb-0">Manage and view business hours for all registered salons.</p>
                    </div>
                    <div className="col-md-6">
                        <InputGroup className="shadow-sm rounded-3 overflow-hidden border-0">
                            <InputGroup.Text className="bg-white border-0"><i className="ri-search-line text-muted"></i></InputGroup.Text>
                            <Form.Control 
                                className="border-0 py-2"
                                placeholder="Search by salon name..." 
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </InputGroup>
                    </div>
                </div>

                <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                    <div className="card-body p-0">
                        <div className="table-responsive">
                            <table className="table table-hover mb-0">
                                <thead style={{ backgroundColor: '#f1f5f9' }}>
                                    <tr className="text-uppercase small fw-bold text-muted">
                                        <th className="px-4 py-3">Salon Information</th>
                                        <th className="px-4 py-3 text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredSalons.map((salon) => (
                                        <tr key={salon.salonId}>
                                            <td className="px-4 py-3">
                                                <div className="d-flex align-items-center">
                                                    <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '45px', height: '45px', backgroundColor: '#ecfdf5', color: THEME_COLOR }}>
                                                        <i className="ri-store-2-line fs-4"></i>
                                                    </div>
                                                    <div>
                                                        <div className="fw-bold text-dark fs-6">{salon.name}</div>
                                                        <div className="small text-muted">{salon.email || 'No email provided'}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-end">
                                                <button className="btn btn-sm rounded-pill px-3 me-2 border-0" style={{ backgroundColor: '#eff6ff', color: '#2563eb' }} onClick={() => handleAction(salon, 'view')}>
                                                    <i className="ri-eye-line me-1"></i> View
                                                </button>
                                                <button className="btn btn-sm rounded-pill px-3 me-2 border-0" style={{ backgroundColor: '#f0fdf4', color: THEME_COLOR }} onClick={() => handleAction(salon, 'edit')}>
                                                    <i className="ri-pencil-line me-1"></i> Edit
                                                </button>
                                                <button className="btn btn-sm rounded-pill px-3 border-0" style={{ backgroundColor: '#fef2f2', color: '#dc2626' }} onClick={() => handleAction(salon, 'delete')}>
                                                    <i className="ri-delete-bin-line me-1"></i> Clear
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* VIEW MODAL */}
                <Modal show={showViewModal} onHide={() => setShowViewModal(false)} centered>
                    <Modal.Header closeButton className="border-0 pb-0">
                        <Modal.Title className="fw-bold fs-4">Schedule Overview</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="pt-2">
                        <div className="text-center mb-4">
                            <div className="badge px-3 py-2 rounded-pill mb-2" style={{ backgroundColor: '#ecfdf5', color: THEME_COLOR }}>{selectedSalon?.name}</div>
                        </div>
                        <div className="list-group list-group-flush border rounded-4 overflow-hidden">
                            {currentTimings.map((day, idx) => (
                                <div key={idx} className="list-group-item d-flex justify-content-between align-items-center py-3">
                                    <span className="fw-bold text-secondary">{daysOfWeekMap[idx].name}</span>
                                    {day.isClosed ? 
                                        <span className="badge bg-danger-subtle text-danger rounded-pill">Closed</span> : 
                                        <span className="fw-semibold text-dark">{formatTime12h(day.openTime)} - {formatTime12h(day.closeTime)}</span>
                                    }
                                </div>
                            ))}
                        </div>
                    </Modal.Body>
                </Modal>

                {/* EDIT MODAL */}
                <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered>
                    <Modal.Header closeButton className="border-0">
                        <Modal.Title className="fw-bold">Modify Business Hours</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="pt-0">
                        <div className="alert alert-info border-0 rounded-4 py-2 small mb-4">
                            <i className="ri-information-line me-2"></i> Update opening and closing times for <b>{selectedSalon?.name}</b>.
                        </div>
                        <div className="table-responsive">
                            <table className="table align-middle">
                                <thead>
                                    <tr className="small text-muted text-uppercase">
                                        <th>Day</th>
                                        <th>Status</th>
                                        <th>Open Time</th>
                                        <th>Close Time</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentTimings.map((day, index) => (
                                        <tr key={index} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td className="fw-bold text-secondary">{daysOfWeekMap[index].name}</td>
                                            <td>
                                                <div className="form-check form-switch">
                                                    <input className="form-check-input" type="checkbox" checked={!day.isClosed} 
                                                        onChange={(e) => {
                                                            const updated = [...currentTimings];
                                                            updated[index].isClosed = !e.target.checked;
                                                            setCurrentTimings(updated);
                                                        }} />
                                                </div>
                                            </td>
                                            <td>
                                                <Form.Control size="sm" type="time" value={day.openTime || ''} disabled={day.isClosed} className="rounded-3 border-light shadow-sm"
                                                    onChange={(e) => {
                                                        const updated = [...currentTimings];
                                                        updated[index].openTime = e.target.value;
                                                        setCurrentTimings(updated);
                                                    }} />
                                            </td>
                                            <td>
                                                <Form.Control size="sm" type="time" value={day.closeTime || ''} disabled={day.isClosed} className="rounded-3 border-light shadow-sm"
                                                    onChange={(e) => {
                                                        const updated = [...currentTimings];
                                                        updated[index].closeTime = e.target.value;
                                                        setCurrentTimings(updated);
                                                    }} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0">
                        <Button variant="light" className="rounded-pill px-4" onClick={() => setShowEditModal(false)}>Cancel</Button>
                        <Button disabled={btnLoading} className="rounded-pill px-5 border-0 shadow-sm" style={{ backgroundColor: THEME_COLOR }} onClick={handleSave}>
                            {btnLoading ? 'Saving...' : 'Update Schedule'}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </div>
    );
};

export default SalonTimings;